import { PortfolioRepo as portfolioRepo } from "@services/portfolio.repo";
import { CurrencyList } from "@common/enums/Currency";
import { ExternalSourcesList } from "@common/enums/ExternalSources";
import * as _ from "lodash";
import { AssetClassNames } from "@common/enums/AssetClassType";
import { exportToCSV } from "@common/utils/exportToCSV";
import { extractErrorMessage } from "@common/utils/errorUtils";

class ClientBalancesComponent implements angular.IOnInit {
  public clientBalancesGridOptions;
  public selectedSecurity;

  public isLoading = false;

  public currencyList = CurrencyList;
  public selectedCurrency;

  public externalSourcesList = ExternalSourcesList;
  public selectedExternalSource;

  public displayAllClients: boolean = false;

  public clientBalances;

  public securities;

  public sortAsc;

  /** @ngInject */
  constructor(
    private $scope: angular.IScope,
    private $window: angular.IWindowService,
    private $state: angular.ui.IStateService,
    private $stateParams: angular.ui.IStateParamsService,
    private portfolioRepo: portfolioRepo,
    private notifyjs
  ) {}

  $onInit = () => {
    this.selectedCurrency = "MUR";
    this.clientBalancesGridOptions = this.$scope["clientBalancesGridOptions"] =
      {
        data: [],
        urlSync: false,
      };

    this.selectedExternalSource = "";

    this.securities = this.getSecuritiesAndCashItems();
  };

  getSelectedSecurityBalancesByClients = () => {
    if (this.selectedSecurity) {
      if (this.selectedSecurity.id > 0) {
        this.getClientBalancesBySecurity();
      } else if (
        this.selectedSecurity.assetClassName === AssetClassNames.CASH
      ) {
        this.getClientBalancesByCurrency();
      }
    }
  };

  getClientBalancesBySecurity = () => {
    this.isLoading = true;
    this.portfolioRepo
      .getClientBalancesForSecurity(
        this.selectedSecurity.id,
        this.selectedCurrency,
        this.selectedExternalSource,
        null,
        this.displayAllClients
      )
      .then((res) => {
        this.clientBalances = res.data as Array<any>;

        this.clientBalances = _.chain(this.clientBalances)
          .groupBy("clientId")
          .map((balances, clientId) => {
            return {
              clientId: clientId,
              clientNumber: _.first(balances).clientNumber,
              clientName: _.first(balances).clientName,
              investmentOfficer: _.first(balances).investmentOfficer,
              relationshipManager: _.first(balances).relationshipManager,
              displayCurrency: this.selectedCurrency,
              currency: _.first(balances).currency,
              description: _.first(balances).description,
              isin: _.first(balances).isin,
              totalMarketValue: balances.reduce((prev, next) => {
                return prev + next.marketValue;
              }, 0),
              quantity: balances.reduce((prev, next) => {
                return prev + next.quantity;
              }, 0),
              totalNav: balances.reduce((prev, next) => {
                return prev + next.totalNav;
              }, 0),
              totalMarketValueInAssetClass: balances.reduce((prev, next) => {
                return prev + next.totalMarketValueInAssetClass;
              }, 0),
              price: this.getPrice(balances),
              costPrice: this.getAverageCostPrice(balances),
              portfolios: balances,
              displayPortfolios: false,
            };
          })
          .sortBy("totalMarketValue")
          .reverse()
          .value();

        this.clientBalancesGridOptions.data = this.clientBalances;
      })
      .catch((err) => {
        this.notifyjs(
          "Failed to load client balances for selected security " + err.data
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  getAverageCostPrice = (portfolioBalances) => {
    let totalPortfoliosWithSecurity = portfolioBalances.filter(
      (pb) => pb.costPrice > 0
    );

    if (totalPortfoliosWithSecurity.length > 0) {
      let totalCostPrice = totalPortfoliosWithSecurity.reduce((prev, next) => {
        return prev + next.costPrice;
      }, 0);

      return +(totalCostPrice / totalPortfoliosWithSecurity.length).toFixed(2);
    }
    return 0;
  };

  getPrice = (portfolioBalances) => {
    let totalPortfoliosWithSecurity = portfolioBalances.filter(
      (pb) => pb.price > 0
    ) as Array<any>;

    if (totalPortfoliosWithSecurity.length > 0) {
      return _.first(totalPortfoliosWithSecurity).price;
    }

    return 0;
  };

  getTotalPercentageOfAssetClass = (clientBalance) => {
    return (
      (clientBalance.totalMarketValue * 100) /
      clientBalance.totalMarketValueInAssetClass
    ).toFixed(2);
  };

  getTotalPercentageOfNAV = (clientBalance) => {
    return (
      (clientBalance.totalMarketValue * 100) /
      clientBalance.totalNav
    ).toFixed(2);
  };

  getPortfolioPercentageOfAssetClass = (portfolioBalance) => {
    return (
      (portfolioBalance.marketValue * 100) /
      portfolioBalance.totalMarketValueInAssetClass
    ).toFixed(2);
  };
  getPortfolioPercentage = (portfolioBalance) => {
    return (
      (portfolioBalance.marketValue * 100) /
      portfolioBalance.totalNav
    ).toFixed(2);
  };

  getSecuritiesAndCashItems = () => {
    this.portfolioRepo
      .getSecuritiesAndCash()
      .then((res) => {
        this.securities = res.data as Array<any>;
      })
      .catch((err) => {
        this.notifyjs("Failed to load securities " + extractErrorMessage(err));
      })
      .finally(() => {
        this.isLoading = false;
        // set the default security
        let securityParam = this.$stateParams["securityId"];
        if (securityParam && +securityParam > -1) {
          this.selectedSecurity = _.first(
            this.securities.filter((security) => {
              return security.id == securityParam;
            })
          );
        } else if (securityParam && _.isString(securityParam)) {
          this.selectedSecurity = _.first(
            this.securities.filter((security) => {
              return (
                security.assetClassName === AssetClassNames.CASH &&
                security.currency == securityParam
              );
            })
          );
        }
        this.getSelectedSecurityBalancesByClients();
      });
  };

  getClientBalancesByCurrency = () => {
    this.isLoading = true;
    this.portfolioRepo
      .getClientBalancesForCash(
        this.selectedSecurity.currency,
        this.selectedCurrency,
        this.selectedExternalSource,
        null,
        this.displayAllClients
      )
      .then((res) => {
        this.clientBalances = res.data as Array<any>;

        this.clientBalances = _.chain(this.clientBalances)
          .groupBy("clientId")
          .map((balances, clientId) => {
            return {
              clientId: clientId,
              clientNumber: _.first(balances).clientNumber,
              clientName: _.first(balances).clientName,
              investmentOfficer: _.first(balances).investmentOfficer,
              relationshipManager: _.first(balances).relationshipManager,
              displayCurrency: this.selectedCurrency,
              currency: _.first(balances).currency,
              description: _.first(balances).description,
              isin: _.first(balances).isin,
              totalMarketValue: balances.reduce((prev, next) => {
                return prev + next.marketValue;
              }, 0),
              quantity: balances.reduce((prev, next) => {
                return prev + next.quantity;
              }, 0),
              totalNav: balances.reduce((prev, next) => {
                return prev + next.totalNav;
              }, 0),
              totalMarketValueInAssetClass: balances.reduce((prev, next) => {
                return prev + next.totalMarketValueInAssetClass;
              }, 0),
              price: this.getPrice(balances),
              costPrice: this.getAverageCostPrice(balances),
              portfolios: balances,
              displayPortfolios: false,
            };
          })
          .sortBy("totalMarketValue")
          .reverse()
          .value();
      })
      .catch((err) => {
        this.notifyjs(
          "Failed to load client balances for selected security " + err.data
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  goToClientPortfolio = ($event, clientNumber) => {
    $event.stopPropagation();
    var url = this.$state.href("root.portfolioManagement.clients.portfolio", {
      clientNumber: clientNumber,
    });
    this.$window.open(url, "_blank");
  };

  exportToCSV = () => {
    let flattenedArray = _.flatten(
      _.map(this.clientBalances, (clientBalance) => {
        return _.flatten(
          _.map(clientBalance.portfolios, (portfolioBalance) => {
            return {
              clientId: clientBalance.clientId,
              clientNumber: clientBalance.clientNumber,
              clientName: clientBalance.clientName,
              investmentOfficer: clientBalance.investmentOfficer,
              relationshipManager: clientBalance.relationshipManager,
              displayCurrency: this.selectedCurrency,
              currency: clientBalance.currency,
              description: clientBalance.description,
              isin: clientBalance.isin,
              totalQuantity: clientBalance.quantity,
              totalMarketValue: clientBalance.totalMarketValue,
              totalPercentageOfAssetClass:
                this.getTotalPercentageOfAssetClass(clientBalance),
              totalMarketValueInAssetClass:
                clientBalance.totalMarketValueInAssetClass,
              totalPercentageOfNAV: this.getTotalPercentageOfNAV(clientBalance),
              totalNav: clientBalance.totalNav,
              price: clientBalance.price,
              averageCostPrice: clientBalance.costPrice,
              accountNumber: portfolioBalance.accountNumber,
              accountSource: portfolioBalance.accountSource,
              quantity: portfolioBalance.quantity,
              costPrice: portfolioBalance.costPrice,
              marketValue: portfolioBalance.marketValue,
              percentageOfAssetClass:
                this.getPortfolioPercentageOfAssetClass(portfolioBalance),
              portfolioMarketValueInAssetClass:
                portfolioBalance.totalMarketValueInAssetClass,
              portfolioNav: portfolioBalance.totalNav,
              percentageOfNAV: this.getPortfolioPercentage(portfolioBalance),
            };
          })
        );
      })
    );

    exportToCSV(flattenedArray, "ClientBalances", true);
  };

  sortClientBalances = (fieldName) => {
    this.sortAsc = !this.sortAsc;
    this.clientBalances = _.sortBy(this.clientBalances, fieldName);
    if (!this.sortAsc) {
      this.clientBalances = this.clientBalances.reverse();
    }
  };
}

export const clientBalances: angular.IComponentOptions = {
  template: require("./clientBalances.component.html"),
  controller: ClientBalancesComponent,
};



// WEBPACK FOOTER //
// ./src/app/portfoliomanagement/clientBalances/clientBalances.component.ts